// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import { Programme, ProgrammeActivityType, ProgrammeType } from 'src/@types/programme';
import { lowerCase, readableStringFormatter } from 'src/utils/stringFormatter';
import {
  SettingsIcon,
  CO2Icon,
  CreditsIcon,
  TextIcon,
  CustomIcon,
  GlobeIcon,
} from 'src/theme/overrides/CustomIcons';
import { IconBuilding, IconCalculator, IconChart, IconCoin, IconGlob, IconMapPoint, IconMultiMapPoint, IconSettings, IconSmile, IconSquares } from 'src/libraries/icons';
import { NavListProps } from './types';
import { FeatureTypes } from 'src/@types/feature';

// ----------------------------------------------------------------------

const generateRouteChildren = (el: Programme) => {
  const children: { [key: string]: NavListProps[] } = {
    CUSTOMER: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
    MANAGED: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Emissions', path: PATH_DASHBOARD.programs.emissions(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
    MYCARBONCLICK: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Participants', path: PATH_DASHBOARD.programs.participants(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
  }

  let result = children[el?.type];

  if (el?.activityType !== ProgrammeActivityType.FLIGHT) {
    result = result?.filter((item) => item?.title !== 'Emissions');
  }

  if (el?.type === ProgrammeType.MYCARBONCLICK) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.FLIGHT) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.JOURNEY) {
    return result
  }

  return result?.filter((item) => item?.title !== 'Insights');
}

export interface ProgrammeMenu {
  title: string;
  code: string;
  path: string;
  id: string;
  icon: JSX.Element;
  programme: any;
  hideDatePicker?: boolean;
  programmeMenu: NavListProps[];
}

const getProgrammeDefaultSubtitle = (programmeType: ProgrammeType) => {
  switch(programmeType) {
    case ProgrammeType.MANAGED:
      return 'Offsetting activity is processed within your CarbonClick account. You can manage and complete offsets  either for your own impact or on behalf of your customers.';
    case ProgrammeType.CUSTOMER:
      return 'Offsetting is initiated directly by your customers through a connected widget, checkout, or API. These offsets are created automatically when your customers complete their transactions.';
    case ProgrammeType.MYCARBONCLICK:
      return 'Sustainability education and offsetting are delivered through a hosted MyCarbonClick experience, typically white-labelled for your organisation.';
    default:
      return '';
  }
}

export const getProgramMenu = (programmes: Programme[]) => {
  const menu: ProgrammeMenu[] = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const title = readableStringFormatter(el.name || el.type);

    return {
      title: title === 'Mycarbonclick' ? 'My carbonclick' : title,
      code: el.code,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      defaultPath: children[0].path,
      id: `nav-item-programme-${el.code}`,
      icon: <IconSquares />,
      subtitle: el?.subtitle || getProgrammeDefaultSubtitle(el.type),
      programme: el.type,
      programmeMenu: children || [],
    }
  });

  return menu;
};

export const TLSettingsMenuItems = [
  {
    title: 'Metadata',
    path: '#generalSettings', // equals to section id
    icon: <SettingsIcon />,
  },
  {
    title: 'Header',
    path: '#headerSection',
    icon: <TextIcon />,
  },
  {
    title: 'Intro',
    path: '#introSection',
    icon: <GlobeIcon />,
  },
  {
    title: 'Impact',
    path: '#impactSection',
    icon: <CO2Icon />,
  },
  {
    title: 'Explainer',
    path: '#howItWorksSection',
    icon: <CreditsIcon />,
  },
  {
    title: 'Custom',
    path: '#customSection',
    icon: <CustomIcon />,
  },
];

export const merchantStatsMenu = [
  { title: 'Impact', path: PATH_DASHBOARD.communications.impact, id: 'nav-item-impact', icon: <IconChart />, subtitle: 'See how your carbon emissions have been offset over time.' },
  { title: 'Supported projects', path: PATH_DASHBOARD.communications.projects, id: 'nav-item-supported-projects', icon: <IconMapPoint />, subtitle: 'View a list of all the initiatives that have offset your carbon emissions.' },
];

const navConfig = [
  {
    title: 'Projects',
    name: 'projects',
    path: '#',
    children: [
      { title: 'Projects', path: PATH_DASHBOARD.projects.root, id: 'nav-item-projects', icon: <IconMapPoint />, subtitle: 'Browse our verified carbon offsetting projects.' },
      { title: 'Portfolios', path: PATH_DASHBOARD.baskets.root, id: 'nav-item-portfolios', icon: <IconMultiMapPoint />, subtitle: 'Curated portfolios enable a balanced approach to offsetting by bundling multiple projects together.' },
    ],
  },
  {
    title: 'My offsets',
    name: 'my-offsets',
    path: '#',
    children: [
      { title: 'My offsets', path: PATH_DASHBOARD.offsets.transactions, id: 'nav-item-my-offsets', icon: <IconCoin />, subtitle: 'View your past carbon offset purchases.' },
      { title: 'My footprints', featureCode: FeatureTypes.FOOTPRINT, path: PATH_DASHBOARD.offsets.footprints, id: 'nav-item-my-footprints', icon: <IconCalculator />, subtitle: 'Calculate the carbon footprint of and manage your business emissions – from flights, freight, fuel use and more.' },
    ],
  },
  {
    title: 'Programmes',
    name: 'programmes',
    path: '#',
    children: [
      { title: 'Individuals', path: PATH_DASHBOARD.programs.individuals, id: 'nav-item-individuals', icon: <IconSmile />, subtitle: 'View individuals and employees who’ve engaged with your programmes.' },
      { title: 'Business customers', featureCode: FeatureTypes.MANAGE_BUSINESS_CUSTOMER, path: PATH_DASHBOARD.programs.businessCustomers, id: 'nav-item-business-customers', icon: <IconBuilding />, subtitle: 'Manage businesses that have participated in your programmes. Profiles may be added manually or created automatically.' },
      { title: 'Toolkit', featureCode: FeatureTypes.INTERACTIVE_TOOLS, path: PATH_DASHBOARD.programs.toolkit, id: 'nav-item-toolkit', icon: <IconSettings />, subtitle: 'Browse our toolkit.' },
    ],
  },
  {
    title: 'Communications',
    name: 'communications',
    path: '#',
    children: [
      ...merchantStatsMenu,
      { title: 'Landing pages', featureCode: FeatureTypes.LANDING_PAGE, path: PATH_DASHBOARD.communications.landingPages, id: 'nav-item-landing pages', icon: <IconGlob />, subtitle: 'Manage the landing pages your customers use to engage with your programmes.' },
      { title: 'Widgets', featureCode: FeatureTypes.IMPACT_WIDGET || FeatureTypes.CARBONCLICK_TILE_DYNAMIC, path: PATH_DASHBOARD.communications.widgets, id: 'nav-item-widgets', icon: <IconSettings />, subtitle: 'Embed widgets on your website to showcase your carbon offset impact.' },
    ],
  },
  {
    title: 'Settings',
    name: 'settings',
    hideOnDesktop: true,
    path: '#',
    children: [
      { title: 'General', path: PATH_DASHBOARD.settings.business, id: 'nav-item-settings-general', icon: <IconSquares /> },
      { title: 'Theme', path: PATH_DASHBOARD.settings.theme, id: 'nav-item-settings-theme', icon: <IconSquares /> },
      { title: 'Default basket', path: PATH_DASHBOARD.settings.defaultBasket, id: 'nav-item-settings-default-basket', icon: <IconSquares /> },
      { title: 'Billing', path: PATH_DASHBOARD.settings.billing, id: 'nav-item-settings-billing', icon: <IconSquares /> },
      { title: 'Receipt emails', path: PATH_DASHBOARD.settings.emails, id: 'nav-item-settings-emails', icon: <IconSquares /> },
      { title: 'Dashboard', path: PATH_DASHBOARD.settings.dashboard, id: 'nav-item-settings-dashboard', icon: <IconSquares /> },
    ],
  }
];

export const getNavConfig = (programmes: Programme[] = [], merchantFeatures?: { code: string }[]) => {
  const programmeMenuItems = getProgramMenu(programmes);

  const menuWithProgrammes = navConfig.map((section) => {
    if (section.name === 'programmes') {
      return {
        ...section,
        children: [...section.children, ...programmeMenuItems],
      };
    }
    return section;
  })

  const newMenu = menuWithProgrammes.map((section) => {
    const updatedSection = {
      ...section,
      children: section.children?.map((item: any) => {
        return {
          ...item,
          disabled: !item?.featureCode ? false : !merchantFeatures?.find(feature => feature?.code === item?.featureCode)
        }
      }),
    };
    
    return updatedSection;
  })

  return newMenu;
}

export default navConfig;
